import { useEffect, useMemo, useState, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Admin from "@/pages/Admin";
import {
  Menu,
  X,
  Plane,
  Sparkles,
  MapPin,
  Sprout,
  Eye,
  Calculator,
  ArrowRight,
  Check,
  Clock,
  Leaf,
  Shield,
  TrendingUp,
  Star,
  ChevronDown,
  Phone,
  Mail,
  Send,
  MessageCircle,
  ShoppingBag,
  Package,
  Wrench,
  Cpu,
  Battery,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve image URL (supports /api/images/{id} relative paths from backend)
const resolveImg = (url) => {
  if (!url) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// ==== Site context (dynamic content from backend + fallbacks) ====
const DEFAULT_BRAND = {
  brand_name: "SKMA",
  brand_full_name: "PT Sinergi Kalimatra Andalan",
  tagline: "Drone Pertanian",
  whatsapp: "6281234567890",
  email: "halo@skma-drone.co.id",
  address: "Jl. Merdeka No. 88, Jakarta Selatan",
  hours: "Senin - Sabtu, 08.00 - 18.00 WIB",
  certification: "Pilot bersertifikasi Kementerian Perhubungan RI",
  hero_kicker: "Presisi · Cepat · Tersertifikasi",
  hero_title: "Semprot & Petakan Lahan",
  hero_title_highlight: "10x Lebih Cepat",
  hero_title_tail: "dengan Drone Pertanian.",
  hero_subtitle: "Tingkatkan hasil panen hingga 30% dan hemat biaya pestisida & pupuk sampai 40% bersama tim drone profesional.",
  hero_image: "https://images.unsplash.com/photo-1713952160156-bb59cac789a9?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
  stat_1_value: "10.000+",
  stat_1_label: "Hektar Tergarap",
  stat_2_value: "500+",
  stat_2_label: "Jam Terbang",
  stat_3_value: "40%",
  stat_3_label: "Hemat Biaya",
};

const SiteContext = createContext({
  content: DEFAULT_BRAND,
  services: [],
  portfolio: [],
  testimonials: [],
  faqs: [],
});
const useSite = () => useContext(SiteContext);

const waLink = (whatsapp, text) =>
  `https://wa.me/${whatsapp}?text=${encodeURIComponent(text)}`;

// Hook that returns brand values + a scoped waLink(text) helper.
// All landing components use this so the WhatsApp number & brand info
// stay in sync with content edited from admin panel.
const useBrand = () => {
  const { content } = useSite();
  const c = content || DEFAULT_BRAND;
  const BRAND = {
    name: c.brand_name || DEFAULT_BRAND.brand_name,
    fullName: c.brand_full_name || DEFAULT_BRAND.brand_full_name,
    tagline: c.tagline || DEFAULT_BRAND.tagline,
    whatsapp: c.whatsapp || DEFAULT_BRAND.whatsapp,
    email: c.email || DEFAULT_BRAND.email,
    address: c.address || DEFAULT_BRAND.address,
    hours: c.hours || DEFAULT_BRAND.hours,
    certification: c.certification || DEFAULT_BRAND.certification,
  };
  return {
    BRAND,
    content: c,
    waLink: (text) => waLink(BRAND.whatsapp, text),
  };
};

// ==== Service pricing (per hektar) ====
const SERVICES = [
  {
    id: "penyemprotan",
    label: "Penyemprotan Pestisida & Herbisida",
    short: "Penyemprotan",
    icon: Sprout,
    pricePerHa: 250000,
    hoursPerHa: 0.4,
    savingsPct: 40,
    desc: "Semprot presisi 10x lebih cepat dibanding manual, dengan sensor dosing otomatis.",
  },
  {
    id: "pemetaan",
    label: "Pemetaan NDVI & Analisis 3D",
    short: "Pemetaan",
    icon: MapPin,
    pricePerHa: 180000,
    hoursPerHa: 0.25,
    savingsPct: 25,
    desc: "Deteksi kesehatan tanaman via kamera multispektral & pemetaan topografi 3D.",
  },
  {
    id: "penebaran",
    label: "Penebaran Pupuk Granul & Benih",
    short: "Penebaran Pupuk",
    icon: Leaf,
    pricePerHa: 350000,
    hoursPerHa: 0.35,
    savingsPct: 30,
    desc: "Sebar pupuk granul & benih presisi tinggi, hemat 30% dosis pupuk.",
  },
  {
    id: "monitoring",
    label: "Monitoring & Inspeksi Rutin",
    short: "Monitoring",
    icon: Eye,
    pricePerHa: 120000,
    hoursPerHa: 0.15,
    savingsPct: 20,
    desc: "Pengawasan rutin kebun sawit, padi, jagung dengan laporan bulanan.",
  },
];

const PRODUCTS = [
  {
    id: "dji-agras-t55",
    category: "drone",
    name: "DJI Agras T55",
    tagline: "Drone Semprot 50L · Pemetaan RTK",
    price: 285000000,
    priceLabel: "Rp 285 jt",
    badge: "Best Seller",
    specs: ["Tangki 50L", "Radar Omni 360°", "Beban Sebar 50kg"],
    stock: "ready",
    img: "/T55-Agras.jpeg",
  },
  {
    id: "dji-agras-t25",
    category: "drone",
    name: "DJI Agras T25",
    tagline: "Drone Kompak untuk Lahan Kecil–Menengah",
    price: 148000000,
    priceLabel: "Rp 148 jt",
    badge: "Populer",
    specs: ["Tangki 20L", "Lipat Portable", "Cocok 5–50 ha"],
    stock: "ready",
    img: "/T25_spraying.jpg",
  },
  {
    id: "xag-p100",
    category: "drone",
    name: "XAG P100 Pro",
    tagline: "Drone Cerdas AI · Sebar & Semprot",
    price: 235000000,
    priceLabel: "Rp 235 jt",
    badge: null,
    specs: ["Tangki 50L", "AI Autonomy", "Dual-Atomized Nozzle"],
    stock: "preorder",
    img: "/P100_pro.jpg",
  },
  {
    id: "baterai-tb60",
    category: "sparepart",
    name: "Baterai Intelligent TB60",
    tagline: "Baterai 30.000 mAh untuk Agras T30/T40",
    price: 18500000,
    priceLabel: "Rp 18.5 jt",
    badge: "Ready Stock",
    specs: ["30.000 mAh", "Fast Charge 12 mnt", "Garansi 6 bulan"],
    stock: "ready",
    icon: Battery,
    accent: "from-amber-500 to-orange-600",
  },
  {
    id: "nozzle-ceramic",
    category: "sparepart",
    name: "Nozzle Keramik Presisi (Set 8)",
    tagline: "Sprayer Ceramic Anti-Aus untuk semua Agras",
    price: 2450000,
    priceLabel: "Rp 2.45 jt",
    badge: null,
    specs: ["Bahan ZrO₂", "Umur 3x nozzle plastik", "Isi 8 pcs + O-ring"],
    stock: "ready",
    icon: Sparkles,
    accent: "from-sky-500 to-teal-600",
  },
  {
    id: "propeller-set",
    category: "sparepart",
    name: "Propeller Set Karbon 38 inch",
    tagline: "Baling-baling Cadangan Agras T40/T50",
    price: 1850000,
    priceLabel: "Rp 1.85 jt",
    badge: null,
    specs: ["Karbon Fiber 3K", "Balanced Factory", "1 set (4 pcs)"],
    stock: "soldout",
    icon: Wrench,
    accent: "from-slate-700 to-slate-900",
  },
  {
    id: "charger-station",
    category: "aksesoris",
    name: "Charging Station Portable 8000W",
    tagline: "Stasiun Pengisian Cepat Lapangan",
    price: 42500000,
    priceLabel: "Rp 42.5 jt",
    badge: null,
    specs: ["8000W Output", "Inlet Generator/PLN", "IP54 Weather-Proof"],
    stock: "preorder",
    icon: Cpu,
    accent: "from-emerald-600 to-emerald-900",
  },
  {
    id: "rtk-base",
    category: "aksesoris",
    name: "RTK Base Station D-RTK 3",
    tagline: "Akurasi Sentimeter untuk Pemetaan",
    price: 38500000,
    priceLabel: "Rp 38.5 jt",
    badge: null,
    specs: ["Akurasi 1 cm", "Range 12 km", "Tripod & case included"],
    stock: "ready",
    icon: MapPin,
    accent: "from-rose-500 to-red-700",
  },
];

const STOCK_META = {
  ready: { label: "Ready Stock", color: "bg-emerald-500 text-white", dot: "bg-emerald-300" },
  preorder: { label: "Pre-Order", color: "bg-amber-500 text-white", dot: "bg-amber-200" },
  soldout: { label: "Sold Out", color: "bg-slate-800 text-white", dot: "bg-slate-400" },
};

const PRODUCT_CATEGORIES = [
  { id: "all", label: "Semua", icon: ShoppingBag },
  { id: "drone", label: "Drone", icon: Plane },
  { id: "sparepart", label: "Sparepart", icon: Wrench },
  { id: "aksesoris", label: "Aksesoris", icon: Cpu },
];

const COMMODITIES = [
  { id: "padi", label: "Padi", mult: 1.0 },
  { id: "sawit", label: "Kelapa Sawit", mult: 1.15 },
  { id: "jagung", label: "Jagung", mult: 1.05 },
  { id: "tebu", label: "Tebu", mult: 1.1 },
  { id: "hortikultura", label: "Hortikultura", mult: 1.2 },
];

const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

// ==== Navbar ====
const Navbar = () => {
  const { BRAND, waLink } = useBrand();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#layanan", label: "Layanan", testid: "nav-link-layanan" },
    { href: "#shop", label: "Shop", testid: "nav-link-shop" },
    { href: "#kalkulator", label: "Estimator", testid: "nav-link-kalkulator" },
    { href: "#portofolio", label: "Portofolio", testid: "nav-link-portofolio" },
    { href: "#booking", label: "Booking", testid: "nav-link-booking" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <a
          href="#top"
          data-testid="nav-brand-logo"
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/logo-SKMA.png"
            alt="Logo"
            className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-white object-contain shadow-md ring-1 ring-black/10 transition-transform group-hover:scale-105"
          />
          <div className="leading-tight">
            <div className={`font-extrabold text-lg ${scrolled ? "text-slate-900" : "text-white"}`}>
              {BRAND.name}
            </div>
            <div className={`text-[10px] uppercase tracking-widest ${scrolled ? "text-emerald-700" : "text-emerald-300"}`}>
              Drone Pertanian
            </div>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className={`text-sm font-medium transition-colors ${
                scrolled ? "text-slate-700 hover:text-emerald-700" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={waLink(`Halo ${BRAND.name}, saya ingin konsultasi layanan drone.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-cta-whatsapp"
            className="btn-shine inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>

        <button
          className={`md:hidden p-2 rounded-lg ${scrolled ? "text-slate-800" : "text-white"}`}
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-${l.testid}`}
                className="py-3 px-3 rounded-lg text-slate-800 hover:bg-emerald-50 font-medium"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <a
              href={waLink(`Halo ${BRAND.name}, saya ingin konsultasi.`)}
              target="_blank"
              rel="noreferrer"
              className="mt-2 bg-emerald-600 text-white text-center font-semibold py-3 rounded-full"
            >
              Konsultasi WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

// ==== Hero ====
const Hero = () => {
  const { BRAND, waLink, content } = useBrand();
  return (
  <section id="top" className="relative overflow-hidden hero-radial grain-overlay pt-28 pb-20 sm:pt-36 sm:pb-32">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-semibold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> {content.hero_kicker}
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
            {content.hero_title} <span className="text-emerald-400">{content.hero_title_highlight}</span> {content.hero_title_tail}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            {content.hero_subtitle} <span className="text-white font-semibold">{BRAND.fullName}</span>
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#kalkulator"
              data-testid="hero-cta-calculator"
              className="btn-shine inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-7 py-4 rounded-full transition-all shadow-xl shadow-emerald-500/30"
            >
              <Calculator className="w-5 h-5" /> Hitung Estimasi Biaya
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={waLink(`Halo ${BRAND.name}, saya butuh konsultasi drone untuk lahan saya.`)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/5 text-white font-semibold px-7 py-4 rounded-full transition-all backdrop-blur"
            >
              <MessageCircle className="w-5 h-5 text-emerald-300" /> Konsultasi WhatsApp
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl">
            {[
              { n: content.stat_1_value, l: content.stat_1_label },
              { n: content.stat_2_value, l: content.stat_2_label },
              { n: content.stat_3_value, l: content.stat_3_label },
            ].map((s) => (
              <div key={s.l} className="glass-card rounded-2xl p-4 sm:p-5">
                <div className="text-2xl sm:text-3xl font-extrabold text-emerald-300">{s.n}</div>
                <div className="text-xs sm:text-sm text-slate-300 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative fade-up">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-emerald-950/50 animate-float">
            <img
              src={resolveImg(content.hero_image) || "/T100_spraying.jpg"}
              alt="Drone pertanian SKMA"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass-card rounded-2xl p-4">
              <div className="flex items-center justify-between text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Live Metric</div>
                  <div className="font-bold text-lg mt-0.5">Efisiensi Lahan +38%</div>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-500/25 flex items-center justify-center border border-emerald-400/40">
                  <TrendingUp className="w-5 h-5 text-emerald-300" />
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-500/20 rounded-full blur-2xl" />
          <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-emerald-500/30 rounded-full blur-3xl" />
        </div>
      </div>
    </div>
  </section>
  );
};

// ==== Services ====
// ==== Services ====
const SERVICE_ICON_MAP = {
  sprout: Sprout,
  mappin: MapPin,
  leaf: Leaf,
  eye: Eye,
  plane: Plane,
  wrench: Wrench,
  battery: Battery,
  cpu: Cpu,
  sparkles: Sparkles,
};

const Services = () => {
  const { services: ctxServices } = useSite();
  const list = ctxServices && ctxServices.length > 0 ? ctxServices : SERVICES;
  return (
  <section id="layanan" className="py-20 sm:py-28 mesh-emerald">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Layanan Kami</div>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
          Solusi drone yang mengubah cara <span className="text-emerald-700">lahan Anda dikelola.</span>
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Dari penyemprotan presisi hingga pemetaan multispektral — semua ditangani tim pilot bersertifikat dan armada drone industri.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {list.map((s, i) => {
          const Icon = typeof s.icon === "string" ? (SERVICE_ICON_MAP[s.icon] || Sprout) : (s.icon || Sprout);
          return (
            <div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className="group relative bg-white rounded-3xl p-7 border border-slate-200 hover:border-emerald-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-600/10 transition-all duration-300"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 group-hover:border-emerald-600 transition-colors">
                <Icon className="w-7 h-7 text-emerald-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">{s.label}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              <div className="mt-5 flex items-center justify-between pt-5 border-t border-slate-100">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Mulai dari</div>
                  <div className="text-emerald-700 font-extrabold">{rupiah(s.pricePerHa)}<span className="text-slate-400 text-xs font-medium">/ha</span></div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

// ==== Shop (Penjualan Drone & Sparepart) ====
const PRODUCT_ICONS = {
  "baterai-tb60": Battery,
  "nozzle-ceramic": Sparkles,
  "propeller-set": Wrench,
  "charger-station": Cpu,
  "rtk-base": MapPin,
};

const Shop = () => {
  const { BRAND, waLink } = useBrand();
  const [filter, setFilter] = useState("all");
  const [products, setProducts] = useState(PRODUCTS);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setProducts(r.data.map((p) => ({ ...p, icon: PRODUCT_ICONS[p.id] || Package })));
        }
      })
      .catch(() => {});
    axios.get(`${API}/bundles`).then((r) => setBundles(r.data || [])).catch(() => {});
  }, []);

  const list = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="shop" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-700">
              <Package className="w-3.5 h-3.5" /> Penjualan Drone & Sparepart
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              Beli drone & suku cadang <span className="text-emerald-700">langsung dari distributor.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Kami mensuplai drone pertanian, baterai, propeller, nozzle, hingga charging station — semua tersertifikasi dan bergaransi resmi.
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="shop-filter-tabs">
            {PRODUCT_CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = filter === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setFilter(c.id)}
                  data-testid={`shop-filter-${c.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                    active
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-600/25"
                      : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-700"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {list.map((p) => {
            const ProductIcon = p.icon;
            return (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="group bg-white rounded-3xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-2xl hover:shadow-slate-300/40 hover:border-emerald-500 transition-all duration-300 flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                {p.img ? (
                  <img
                    src={resolveImg(p.img)}
                    alt={p.name}
                    loading="lazy"
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${p.stock === "soldout" ? "grayscale" : ""}`}
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${p.accent || "from-emerald-600 to-emerald-900"} flex items-center justify-center relative overflow-hidden ${p.stock === "soldout" ? "grayscale" : ""}`}>
                    <div className="absolute inset-0 opacity-20" style={{
                      backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,0,0,0.2), transparent 50%)"
                    }} />
                    {ProductIcon && (
                      <ProductIcon className="w-24 h-24 text-white/95 relative z-10 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.4} />
                    )}
                  </div>
                )}
                {p.badge && p.stock !== "soldout" && (
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                    {p.badge}
                  </div>
                )}
                {/* Stock badge - always shown, top-left prioritized over badge */}
                {p.stock && (
                  <div
                    data-testid={`product-stock-${p.id}`}
                    className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md ${STOCK_META[p.stock].color}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${STOCK_META[p.stock].dot} ${p.stock === "ready" ? "animate-pulse" : ""}`} />
                    {STOCK_META[p.stock].label}
                  </div>
                )}
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow">
                  {p.category === "drone" && <Plane className="w-4 h-4 text-emerald-700" />}
                  {p.category === "sparepart" && <Wrench className="w-4 h-4 text-emerald-700" />}
                  {p.category === "aksesoris" && <Cpu className="w-4 h-4 text-emerald-700" />}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                  {p.category === "drone" ? "Drone" : p.category === "sparepart" ? "Sparepart" : "Aksesoris"}
                </div>
                <h3 className="mt-1.5 text-base font-bold text-slate-900 leading-snug">{p.name}</h3>
                <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.tagline}</p>

                <ul className="mt-3 space-y-1.5">
                  {p.specs.map((sp) => (
                    <li key={sp} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                      <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{sp}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Harga</div>
                    <div className="text-emerald-700 font-extrabold text-lg leading-tight">{p.priceLabel}</div>
                  </div>
                  {p.stock === "soldout" ? (
                    <span
                      data-testid={`product-order-${p.id}`}
                      className="inline-flex items-center gap-1.5 bg-slate-200 text-slate-500 text-xs font-bold px-4 py-2.5 rounded-full cursor-not-allowed"
                    >
                      Habis
                    </span>
                  ) : (
                    <a
                      href={waLink(`Halo ${BRAND.name}, saya tertarik ${p.stock === "preorder" ? "pre-order" : "membeli"} produk:%0A%0A${p.name}%0AHarga: ${p.priceLabel}%0AStatus: ${STOCK_META[p.stock].label}%0A%0AMohon info stok, garansi, dan skema pembayaran. Terima kasih.`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-order-${p.id}`}
                      className={`inline-flex items-center gap-1.5 text-white text-xs font-bold px-4 py-2.5 rounded-full transition-colors ${
                        p.stock === "preorder"
                          ? "bg-amber-500 hover:bg-amber-600"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> {p.stock === "preorder" ? "Pre-Order" : "Pesan"}
                    </a>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>

        {/* Bundle Packages */}
        {bundles.length > 0 && (
          <div className="mt-16" data-testid="bundles-section">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-amber-700">
                  <Sparkles className="w-3.5 h-3.5" /> Paket Bundling
                </div>
                <h3 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                  Hemat lebih dengan <span className="text-amber-600">paket bundling siap operasi.</span>
                </h3>
                <p className="mt-2 text-slate-600 text-sm">Kombinasi drone + baterai + charger dalam satu paket, langsung siap terjun ke lahan.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {bundles.map((b) => (
                <article
                  key={b.id}
                  data-testid={`bundle-card-${b.id}`}
                  className="group relative rounded-3xl overflow-hidden border border-slate-200 bg-white hover:border-amber-400 hover:shadow-2xl hover:shadow-amber-500/10 transition-all"
                >
                  <div className={`relative aspect-[16/9] overflow-hidden bg-gradient-to-br ${b.accent || "from-emerald-600 to-emerald-900"}`}>
                    {b.image && (
                      <img
                        src={resolveImg(b.image)}
                        alt={b.name}
                        loading="lazy"
                        className="w-full h-full object-cover mix-blend-luminosity opacity-70 group-hover:opacity-90 transition-opacity"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                    {b.badge && (
                      <div className="absolute top-4 left-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
                        <Sparkles className="w-3 h-3" /> {b.badge}
                      </div>
                    )}
                    <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white text-amber-700 text-xs font-extrabold shadow-lg">
                      -{b.discount_pct}%
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Paket Hemat</div>
                      <h4 className="mt-1 text-xl sm:text-2xl font-extrabold leading-tight">{b.name}</h4>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-sm text-slate-600 leading-relaxed">{b.tagline}</p>

                    {b.highlights && b.highlights.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {b.highlights.map((h, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-5 pt-5 border-t border-slate-100 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Harga Paket</div>
                        <div className="flex items-baseline gap-2 mt-0.5">
                          <div className="text-emerald-700 font-extrabold text-2xl leading-none">{b.priceLabel}</div>
                          <div className="text-slate-400 text-sm line-through">{b.originalPriceLabel}</div>
                        </div>
                      </div>
                      <a
                        href={`https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(`Halo ${BRAND.name}, saya tertarik dengan paket:\n\n${b.name}\nHarga bundling: ${b.priceLabel} (diskon ${b.discount_pct}%)\n\nMohon info stok, garansi, dan skema pembayaran. Terima kasih.`)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`bundle-order-${b.id}`}
                        className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-5 py-3 rounded-full transition-colors flex-shrink-0"
                      >
                        <MessageCircle className="w-4 h-4" /> Pesan Paket
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-800 rounded-3xl p-8 sm:p-10 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-semibold">
              <Battery className="w-4 h-4" /> Butuh produk lain?
            </div>
            <h3 className="mt-2 text-2xl sm:text-3xl font-extrabold">
              Cari drone khusus, upgrade firmware, atau paket bundling?
            </h3>
            <p className="mt-2 text-emerald-100 text-sm max-w-2xl">
              Tim sales SKMA siap bantu carikan unit sesuai kebutuhan lahan Anda — mulai dari trade-in hingga pembelian kredit.
            </p>
          </div>
          <a
            href={waLink(`Halo ${BRAND.name}, saya butuh konsultasi pembelian drone/sparepart custom.`)}
            target="_blank"
            rel="noreferrer"
            data-testid="shop-consult-cta"
            className="relative btn-shine inline-flex items-center gap-2 bg-white text-emerald-800 hover:bg-emerald-50 font-bold px-6 py-4 rounded-full flex-shrink-0"
          >
            <MessageCircle className="w-5 h-5" /> Konsultasi Sales
          </a>
        </div>
      </div>
    </section>
  );
};

// ==== Calculator ====
const Calculator2 = ({ onBook }) => {
  const { BRAND } = useBrand();
  const { services: ctxServices } = useSite();
  const servicesList = ctxServices && ctxServices.length > 0 ? ctxServices : SERVICES;
  const [hectares, setHectares] = useState(10);
  const [serviceId, setServiceId] = useState(() => servicesList[0]?.id);
  const [commodityId, setCommodityId] = useState("padi");
  const [frequency, setFrequency] = useState("sekali");

  // Keep selected serviceId valid when the list hydrates from API
  useEffect(() => {
    if (!servicesList.find((s) => s.id === serviceId)) {
      setServiceId(servicesList[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesList.length]);

  const service = servicesList.find((s) => s.id === serviceId) || servicesList[0];
  const commodity = COMMODITIES.find((c) => c.id === commodityId);

  const calc = useMemo(() => {
    if (!service) return { total: 0, totalPerHa: 0, hours: "0", days: 1, savings: 0 };
    const base = service.pricePerHa * hectares * commodity.mult;
    const total = frequency === "langganan" ? base * 0.85 * 4 : base;
    const hours = service.hoursPerHa * hectares;
    const days = Math.max(1, Math.ceil(hours / 6));
    return {
      total,
      totalPerHa: total / hectares,
      hours: hours.toFixed(1),
      days,
      savings: service.savingsPct,
    };
  }, [hectares, service, commodity, frequency]);

  const summary = `Halo ${BRAND.name}!%0A%0ASaya tertarik dengan estimasi berikut:%0A- Luas Lahan: ${hectares} ha%0A- Komoditas: ${commodity.label}%0A- Layanan: ${service.label}%0A- Paket: ${frequency === "langganan" ? "Langganan Bulanan (4x)" : "Sekali Jalan"}%0A- Estimasi Biaya: ${rupiah(calc.total)}%0A%0AMohon informasinya, terima kasih.`;

  return (
    <section id="kalkulator" className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Kalkulator Estimasi</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            Hitung biaya drone untuk lahan Anda dalam 10 detik.
          </h2>
          <p className="mt-4 text-slate-600">Geser slider, pilih layanan — dapatkan estimasi live tanpa perlu telepon dulu.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xl shadow-slate-200/50">
            <div>
              <label className="flex justify-between items-baseline mb-3">
                <span className="text-sm font-semibold text-slate-900">Luas Lahan</span>
                <span className="text-emerald-700 font-extrabold text-xl" data-testid="calculator-hectare-display">
                  {hectares} <span className="text-sm font-medium text-slate-500">hektar</span>
                </span>
              </label>
              <input
                type="range"
                min="1"
                max="500"
                value={hectares}
                onChange={(e) => setHectares(Number(e.target.value))}
                data-testid="calculator-hectare-input"
                className="w-full accent-emerald-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>1 ha</span>
                <span>500 ha</span>
              </div>
            </div>

            <div className="mt-8">
              <label className="block text-sm font-semibold text-slate-900 mb-3">Jenis Layanan</label>
              <div className="grid grid-cols-2 gap-2" data-testid="calculator-service-select">
                {servicesList.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setServiceId(s.id)}
                    data-testid={`calc-service-${s.id}`}
                    className={`text-left p-4 rounded-2xl border-2 transition-all ${
                      serviceId === s.id
                        ? "border-emerald-600 bg-emerald-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="text-sm font-bold text-slate-900">{s.short}</div>
                    <div className="text-xs text-slate-500 mt-1">{rupiah(s.pricePerHa)}/ha</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Komoditas</label>
                <select
                  value={commodityId}
                  onChange={(e) => setCommodityId(e.target.value)}
                  data-testid="calculator-commodity-select"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {COMMODITIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Frekuensi</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  data-testid="calculator-frequency-select"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="sekali">Sekali Jalan</option>
                  <option value="langganan">Langganan Bulanan (4x, diskon 15%)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-2 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800 text-white rounded-3xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Estimasi Total</div>
              <div
                className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight"
                data-testid="calculator-total-price"
              >
                {rupiah(calc.total)}
              </div>
              <div className="text-emerald-200 text-sm mt-1">
                ≈ {rupiah(calc.totalPerHa)} per hektar
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-200 uppercase tracking-wider">Durasi</div>
                    <div className="font-semibold">{calc.hours} jam · ± {calc.days} hari kerja</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-300" />
                  </div>
                  <div>
                    <div className="text-xs text-emerald-200 uppercase tracking-wider">Estimasi Hemat</div>
                    <div className="font-semibold">Hingga {calc.savings}% biaya pestisida/pupuk</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={`https://wa.me/${BRAND.whatsapp}?text=${summary}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="calculator-whatsapp-button"
                  className="btn-shine inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-4 rounded-full transition-colors"
                >
                  <MessageCircle className="w-5 h-5" /> Pesan Paket via WhatsApp
                </a>
                <button
                  onClick={() =>
                    onBook({
                      service: service.label,
                      hectares,
                      commodity: commodity.label,
                      estimated_price: calc.total,
                    })
                  }
                  data-testid="calculator-book-button"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-4 rounded-full transition-colors"
                >
                  Isi Form Booking Lengkap <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==== Portfolio ====
const PORTFOLIO = [
  {
    id: "sawit-kalimantan",
    tag: "Perkebunan Sawit",
    title: "Perkebunan Sawit 850 Ha, Kalimantan Barat",
    metric: "Dosis pestisida -42%, biaya operasional -35%",
    img: "https://images.unsplash.com/photo-1559628233-100c798642d4?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80",
  },
  {
    id: "padi-jabar",
    tag: "Sawah Padi",
    title: "Sawah Padi 320 Ha, Karawang Jawa Barat",
    metric: "Waktu semprot 6 jam vs 4 hari manual",
    img: "https://images.unsplash.com/photo-1519082572439-7ed19908e47e?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80",
  },
  {
    id: "jagung-lampung",
    tag: "Kebun Jagung",
    title: "Kebun Jagung 210 Ha, Lampung Tengah",
    metric: "Hasil panen +28% berkat pemetaan NDVI",
    img: "https://images.pexels.com/photos/34182367/pexels-photo-34182367.jpeg?auto=compress&cs=tinysrgb&w=1000",
  },
];

const Portfolio = () => {
  const { BRAND, waLink } = useBrand();
  const { portfolio: ctxPortfolio } = useSite();
  const list = ctxPortfolio && ctxPortfolio.length > 0 ? ctxPortfolio : PORTFOLIO;
  return (
  <section id="portofolio" className="py-20 sm:py-28 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Portofolio</div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight max-w-2xl">
            Ribuan hektar sudah <span className="text-emerald-700">merasakan hasilnya.</span>
          </h2>
        </div>
        <a
          href={waLink(`Halo ${BRAND.name}, saya ingin lihat detail portofolio lengkap.`)}
          target="_blank"
          rel="noreferrer"
          className="hidden sm:inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-900 font-semibold"
        >
          Lihat semua case study <ArrowRight className="w-4 h-4" />
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {list.map((p) => (
          <article
            key={p.id}
            data-testid={`portfolio-item-${p.id}`}
            className="group relative rounded-3xl overflow-hidden aspect-[4/5] cursor-pointer"
          >
            <img
              src={resolveImg(p.img)}
              alt={p.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/50 to-transparent" />
            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
              <div className="inline-flex self-start items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/90 text-xs font-semibold mb-3">
                <Leaf className="w-3 h-3" /> {p.tag}
              </div>
              <h3 className="text-lg sm:text-xl font-bold leading-tight">{p.title}</h3>
              <div className="mt-3 flex items-center gap-2 text-emerald-300 text-sm font-semibold">
                <TrendingUp className="w-4 h-4" /> {p.metric}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

// ==== Testimonials ====
const TESTIMONIALS = [
  {
    name: "Pak Suwarno",
    role: "Ketua Kelompok Tani, Karawang",
    body: "Sebelum pakai drone SKMA, semprot 40 ha butuh 3 hari. Sekarang cukup 4 jam. Hasil panen juga lebih rata.",
  },
  {
    name: "Ibu Retno",
    role: "Manajer Kebun Sawit, Kalimantan",
    body: "Laporan NDVI dari SKMA sangat detail. Kami bisa deteksi area stres tanaman lebih cepat dan tepat.",
  },
  {
    name: "Bpk. Ahmad F.",
    role: "Pemilik Kebun Jagung, Lampung",
    body: "Tim SKMA profesional, on-time, dan biaya jauh lebih efisien dibanding sewa pekerja manual.",
  },
];

const Testimonials = () => {
  const { testimonials: ctx } = useSite();
  const list = ctx && ctx.length > 0 ? ctx : TESTIMONIALS;
  return (
  <section className="py-20 sm:py-28 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-14">
        <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Testimoni</div>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
          Kata mereka yang lahannya sudah <span className="text-emerald-700">naik kelas.</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {list.map((t, i) => (
          <div key={t.id || i} className="bg-white rounded-3xl p-7 border border-slate-200 relative">
            <div className="flex gap-0.5 text-amber-500 mb-4">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} className="w-4 h-4 fill-current" />
              ))}
            </div>
            <p className="text-slate-700 leading-relaxed">&ldquo;{t.body}&rdquo;</p>
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="font-bold text-slate-900">{t.name}</div>
              <div className="text-sm text-slate-500">{t.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

// ==== Booking ====
const Booking = ({ prefill, clearPrefill }) => {
  const { BRAND } = useBrand();
  const { services: ctxServices } = useSite();
  const servicesList = ctxServices && ctxServices.length > 0 ? ctxServices : SERVICES;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    location: "",
    service: servicesList[0]?.label || SERVICES[0].label,
    hectares: "",
    commodity: COMMODITIES[0].label,
    date: "",
    notes: "",
    estimated_price: null,
  });
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, ...prefill, hectares: String(prefill.hectares || f.hectares) }));
      setTimeout(() => {
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
      }, 60);
    }
  }, [prefill]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, err: "" });
    try {
      const payload = {
        ...form,
        hectares: parseFloat(form.hectares) || 0,
        estimated_price: form.estimated_price ? Number(form.estimated_price) : null,
      };
      await axios.post(`${API}/bookings`, payload);
      setStatus({ loading: false, ok: true, err: "" });

      // Auto forward to WhatsApp
      const msg = `Halo ${BRAND.name}! Saya baru saja submit form booking:%0A%0ANama: ${payload.name}%0ANo. WA: ${payload.phone}%0ALokasi: ${payload.location}%0ALayanan: ${payload.service}%0AKomoditas: ${payload.commodity}%0ALuas: ${payload.hectares} ha%0ATanggal target: ${payload.date || "-"}%0ACatatan: ${payload.notes || "-"}${payload.estimated_price ? `%0AEstimasi: ${rupiah(payload.estimated_price)}` : ""}`;
      setTimeout(() => {
        window.open(`https://wa.me/${BRAND.whatsapp}?text=${msg}`, "_blank");
      }, 400);

      clearPrefill?.();
    } catch (err) {
      setStatus({ loading: false, ok: false, err: "Gagal submit. Coba lagi atau hubungi WhatsApp langsung." });
    }
  };

  return (
    <section id="booking" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Booking Sekarang</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Jadwalkan drone SKMA ke lahan Anda.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Isi form berikut, tim kami akan konfirmasi via WhatsApp maksimal 2 jam kerja. Data langsung terkirim ke agent operasional.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Konfirmasi cepat < 2 jam kerja",
                "Survey lokasi gratis untuk lahan > 50 ha",
                "Garansi ulang penyemprotan bila coverage < 95%",
                "Pilot bersertifikasi & armada terawat",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-700">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-700" />
                  </div>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-3 bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Nama Lengkap / Perusahaan *</label>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="booking-form-name"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Contoh: Pak Budi / PT Tani Jaya"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Nomor WhatsApp *</label>
                <input
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="booking-form-phone"
                  type="tel"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="08xxxxxxxxxx"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-900 mb-2">Lokasi Lahan *</label>
                <input
                  required
                  value={form.location}
                  onChange={update("location")}
                  data-testid="booking-form-location"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Kabupaten / Kota, Provinsi"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Jenis Layanan</label>
                <select
                  value={form.service}
                  onChange={update("service")}
                  data-testid="booking-form-service"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {servicesList.map((s) => (
                    <option key={s.id} value={s.label}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Komoditas</label>
                <select
                  value={form.commodity}
                  onChange={update("commodity")}
                  data-testid="booking-form-commodity"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {COMMODITIES.map((c) => (
                    <option key={c.id} value={c.label}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Estimasi Luas (Ha) *</label>
                <input
                  required
                  value={form.hectares}
                  onChange={update("hectares")}
                  data-testid="booking-form-hectares"
                  type="number"
                  min="1"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Contoh: 25"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Tanggal Target</label>
                <input
                  value={form.date}
                  onChange={update("date")}
                  data-testid="booking-form-date"
                  type="date"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-slate-900 mb-2">Catatan Tambahan</label>
                <textarea
                  value={form.notes}
                  onChange={update("notes")}
                  data-testid="booking-form-notes"
                  rows="3"
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Detail kondisi lahan, akses jalan, dll."
                />
              </div>
            </div>

            {status.ok && (
              <div
                data-testid="booking-success"
                className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center gap-3"
              >
                <Check className="w-5 h-5" />
                <span>Booking terkirim! Kami sedang membuka WhatsApp untuk konfirmasi cepat.</span>
              </div>
            )}
            {status.err && (
              <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800">
                {status.err}
              </div>
            )}

            <button
              type="submit"
              disabled={status.loading}
              data-testid="booking-form-submit-button"
              className="btn-shine mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors"
            >
              {status.loading ? "Mengirim..." : (<><Send className="w-5 h-5" /> Kirim & Lanjut ke WhatsApp</>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// ==== FAQ ====
const FAQS = [
  {
    q: "Berapa kapasitas tangki drone SKMA?",
    a: "Armada drone kami memiliki kapasitas tangki 20-40 liter untuk cairan dan hingga 50 kg untuk pupuk granul, cukup untuk 2-4 hektar per pengisian.",
  },
  {
    q: "Apakah tim SKMA membawa generator sendiri?",
    a: "Ya, seluruh tim operasional kami dilengkapi charging station portable, generator, dan tim teknis untuk memastikan operasi tetap berjalan meski akses listrik terbatas.",
  },
  {
    q: "Area mana saja yang terjangkau?",
    a: "Saat ini kami melayani Jawa, Sumatera, Kalimantan, dan Sulawesi. Untuk area terpencil, tersedia paket mobilisasi khusus.",
  },
  {
    q: "Bagaimana cara pembayaran?",
    a: "Kami menerima transfer bank, virtual account, dan invoice kredit untuk klien korporat. DP 30% saat konfirmasi, pelunasan setelah pekerjaan selesai.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  const { faqs: ctx } = useSite();
  const list = ctx && ctx.length > 0 ? ctx : FAQS;
  return (
    <section className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest font-semibold text-emerald-700">FAQ</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Pertanyaan yang sering ditanyakan
          </h2>
        </div>
        <div className="space-y-3">
          {list.map((f, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between p-5 sm:p-6 text-left"
              >
                <span className="font-semibold text-slate-900 pr-4">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-emerald-700 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-slate-600 leading-relaxed">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==== Footer ====
const Footer = () => {
  const { BRAND } = useBrand();
  const { services: ctxServices } = useSite();
  const services = ctxServices && ctxServices.length > 0 ? ctxServices : SERVICES;
  return (
  <footer className="bg-emerald-950 text-slate-300 pt-16 pb-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-10 pb-10 border-b border-white/10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Plane className="w-5 h-5 text-white -rotate-45" />
            </div>
            <div className="leading-tight">
              <div className="font-extrabold text-white text-lg">{BRAND.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400">{BRAND.tagline}</div>
            </div>
          </div>
          <p className="mt-5 max-w-md text-sm leading-relaxed">
            {BRAND.fullName} — solusi drone pertanian presisi untuk penyemprotan, pemetaan lahan, penebaran pupuk, dan monitoring rutin.
          </p>
          <div className="mt-5 flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span>{BRAND.certification}</span>
          </div>
        </div>

        <div>
          <div className="text-white font-semibold mb-4">Layanan</div>
          <ul className="space-y-2 text-sm">
            {services.map((s) => (
              <li key={s.id}>
                <a href="#layanan" className="hover:text-white transition-colors">{s.short}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-white font-semibold mb-4">Kontak</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span>{BRAND.address}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span>{BRAND.hours}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Phone className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <a href={`https://wa.me/${BRAND.whatsapp}`} className="hover:text-white">+{BRAND.whatsapp}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="pt-8 text-sm flex flex-col sm:flex-row justify-between gap-3 text-slate-400">
        <div>© 2026 {BRAND.fullName}. All rights reserved.</div>
        <div>Made with <Leaf className="w-3.5 h-3.5 inline text-emerald-400" /> for Indonesian farmers.</div>
      </div>
    </div>
  </footer>
  );
};

// ==== Floating WhatsApp ====
const FloatingWhatsApp = () => {
  const { BRAND, waLink } = useBrand();
  const [showTip, setShowTip] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowTip(true), 3000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed bottom-5 right-5 z-40 flex items-end gap-3">
      {showTip && (
        <div
          className="hidden sm:block bg-white shadow-xl rounded-2xl px-4 py-3 border border-slate-200 max-w-xs text-sm text-slate-700 relative fade-up"
        >
          <button
            onClick={() => setShowTip(false)}
            className="absolute top-1 right-1 text-slate-400 hover:text-slate-700"
            aria-label="close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          Halo Pak/Bu! Butuh bantuan hitung kuota drone untuk lahan Anda?
        </div>
      )}
      <a
        href={waLink(`Halo ${BRAND.name}, saya butuh info drone pertanian.`)}
        target="_blank"
        rel="noreferrer"
        data-testid="floating-whatsapp-btn"
        className="pulse-ring w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#128C7E] flex items-center justify-center shadow-2xl shadow-emerald-900/30 transition-colors"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>
    </div>
  );
};

// ==== Root App ====
function LandingPage() {
  const [prefill, setPrefill] = useState(null);
  const [siteData, setSiteData] = useState({
    content: DEFAULT_BRAND,
    services: [],
    portfolio: [],
    testimonials: [],
    faqs: [],
  });

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});

    Promise.all([
      axios.get(`${API}/site-content`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/services`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/portfolio`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/testimonials`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/faqs`).then((r) => r.data).catch(() => []),
    ]).then(([content, services, portfolio, testimonials, faqs]) => {
      setSiteData({
        content: content || DEFAULT_BRAND,
        services: services || [],
        portfolio: portfolio || [],
        testimonials: testimonials || [],
        faqs: faqs || [],
      });
    });
  }, []);

  return (
    <SiteContext.Provider value={siteData}>
      <div className="App">
        <Navbar />
        <Hero />
        <Services />
        <Shop />
        <Calculator2 onBook={setPrefill} />
        <Portfolio />
        <Testimonials />
        <Booking prefill={prefill} clearPrefill={() => setPrefill(null)} />
        <FAQ />
        <Footer />
        <FloatingWhatsApp />
      </div>
    </SiteContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
